import type {
  OperatorActivity,
  ScreenshotEntry,
  SessionMessageEntry,
} from '@erp/rpc';
import {
  actionDetail,
  NEXT_SCREEN,
} from '@/components/common/agentActionFormatting';

/**
 * A reply the human sent during the interview, tracked locally so it can be
 * shown in the chat. The live `OperatorActivity` stream is agent-side only (it
 * doesn't echo the human's answers), so the page keeps these and the mapper
 * interleaves them. `afterCount` is `activity.messages.length` captured when the
 * reply was sent, which places it chronologically among the agent's messages.
 */
export interface SentReply {
  id: string;
  content: string;
  afterCount: number;
}

/** The slice of `OperatorActivity` the chat is derived from. */
type ChatSource = Pick<
  OperatorActivity,
  'messages' | 'screenshots' | 'awaitingInput'
>;

/** A phase of a mock-UI build, used to segment the thread into collapsible sections. `screen`
 * carries the 0-based storyboard index + total; `setup` is the environment seed and `finalize`
 * is the post-build eval-paths.json authoring. Driven by the `section` progress metadata. */
export type ChatSection =
  | { phase: 'setup' }
  | { phase: 'screen'; index: number; total: number }
  | { phase: 'finalize' };

/** One renderable unit of the interview chat — a discriminated union so
 * `OperatorChatPanel` can dispatch each item to the right component. */
export type ChatItem =
  | { kind: 'human'; id: string; content: string; timestamp: Date }
  | {
      kind: 'note';
      id: string;
      content: string;
      timestamp: Date;
      badge?: string;
    }
  | { kind: 'marker'; id: string; content: string; timestamp: Date }
  | {
      kind: 'section';
      id: string;
      section: ChatSection;
      timestamp: Date;
    }
  | {
      kind: 'system';
      id: string;
      content: string;
      timestamp: Date;
      tone: 'success' | 'failed' | 'skipped';
    }
  | {
      kind: 'turn';
      id: string;
      timestamp: Date;
      reasoning: string | null;
      actions: ReadonlyArray<{
        id: string;
        action: string;
        detail: string | null;
      }>;
      screenshotDataUrl: string | null;
      /** The build phase this turn belongs to (from its actions' `section` metadata), or null for
       * a reasoning-only turn that inherits the current section. */
      section: ChatSection | null;
    };

const SOP_STEP_KIND = 'sop_step';
// A setup procedure (an already-taught SOP) replayed inline before the live teaching turn
// begins — see runSetupProcedureInline in sopSessionRunner.ts. Tagged so these lines can be
// visually set apart from the SOP actually being taught in this interview.
const SETUP_PROCEDURE_START_KIND = 'setup_procedure_start';
const SOP_REPLAY_STEP_KIND = 'sop_replay_step';
const SETUP_PROCEDURE_BADGE = 'Setup procedure';
// Kernel-native pre-login outcome (sopSessionRunner.ts's provisionBrowser) — rendered as a
// system notice, never attributed to the agent's own voice.
const PRE_AUTH_SKIPPED_KIND = 'pre_auth_skipped';
const PRE_AUTH_SUCCEEDED_KIND = 'pre_auth_succeeded';

function isSetupProcedureEntry(entry: SessionMessageEntry): boolean {
  const kind = entry.metadata?.kind;
  return kind === SETUP_PROCEDURE_START_KIND || kind === SOP_REPLAY_STEP_KIND;
}

/** A committed-step entry's clean one-liner, from its `sopStep` metadata when
 * present (`✓ Recorded step N — name`), else the raw "Committed step N: …" text. */
function recordedStepLine(entry: SessionMessageEntry): string {
  const sopStep = entry.metadata?.sopStep;
  if (sopStep && typeof sopStep === 'object') {
    const { index, name } = sopStep as { index?: unknown; name?: unknown };
    if (typeof index === 'number' && typeof name === 'string' && name.trim()) {
      return `✓ Recorded step ${index} — ${name}`;
    }
  }
  return entry.message;
}

/** Pre-auth outcome → notice tone. A successful login is 'success'; an outright failed
 * (likely blocked) automated attempt is 'failed'; every other skip reason (no Start URL,
 * wrong credential type, no secret) is a plain 'skipped' — none of these are errors. */
function preAuthTone(
  entry: SessionMessageEntry,
): 'success' | 'failed' | 'skipped' {
  if (entry.metadata?.kind === PRE_AUTH_SUCCEEDED_KIND) return 'success';
  return entry.metadata?.reason === 'login_failed' ? 'failed' : 'skipped';
}

/** The latest screenshot captured on the same A2A message as `messageId`, if any. */
function screenshotFor(
  messageId: string | undefined,
  screenshots: readonly ScreenshotEntry[],
): string | null {
  if (!messageId) return null;
  let found: string | null = null;
  for (const shot of screenshots) {
    if (shot.messageId === messageId) found = shot.data;
  }
  return found;
}

/** The mock_ui seed StepEvent's action id — surfaced as the "Setup" section. */
const SEED_ACTION = 'seed';

/** The build phase a step message belongs to, from its `section` progress metadata (see
 * ai-service `hooks.py`). Null when the message carries none (e.g. a reasoning bubble). */
function sectionOf(entry: SessionMessageEntry): ChatSection | null {
  const meta = entry.metadata ?? {};
  if (meta.section === 'setup') return { phase: 'setup' };
  if (meta.section === 'finalize') return { phase: 'finalize' };
  if (meta.section === 'screen') {
    return {
      phase: 'screen',
      index: typeof meta.screen === 'number' ? meta.screen : 0,
      total: typeof meta.total === 'number' ? meta.total : 0,
    };
  }
  return null;
}

/** A stable key for grouping — screens differ by index so each is its own section. */
function sectionKey(section: ChatSection): string {
  return section.phase === 'screen' ? `screen:${section.index}` : section.phase;
}

/** One turn's pieces while walking `activity.messages` in order (see `flushTurn`). */
interface OpenTurn {
  id: string;
  timestamp: Date;
  reasoning: string | null;
  actions: Array<{ id: string; action: string; detail: string | null }>;
  screenshotDataUrl: string | null;
  section: ChatSection | null;
}

/**
 * Map the live `operator.author_sop` interview state to the interview chat's `ChatItem[]`.
 * Adjacent reasoning + action entries fold into one `{kind:'turn'}` "turn card" — grouping is
 * by adjacency, NOT by shared `messageId`: `operatorAgUiAdapter.ts`'s live stream already
 * collapses every progress event for one A2A message down to a single `SessionMessageEntry`,
 * so three sequential clicks arrive as three *different* `messageId`s, not one shared id.
 * `messageId` is only used here to join a screenshot (from `activity.screenshots`) to the
 * entry that produced it.
 *
 * - `metadata.kind === "sop_step"` (a captured SOP step) → a `{kind:'marker'}` line.
 * - `metadata.kind === "pre_auth_skipped"|"pre_auth_succeeded"` → a `{kind:'system'}` notice,
 *   never attributed to the agent's own voice.
 * - a setup-procedure-replay entry (a different, already-taught SOP run inline) → unchanged
 *   `{kind:'note', badge:'Setup procedure'}` for its narration; its actions are dropped, as
 *   before — they're a different procedure's telemetry, not this interview's turns.
 * - a reasoning entry starts a new turn (or fills the open one's still-empty reasoning); an
 *   action entry appends to the open turn, opening one if none is open.
 * - each of the three kind-tagged cases above flushes any open turn first, so a marker/notice
 *   never gets visually swallowed into a turn card.
 * - locally-tracked human replies are interleaved at their anchor, also flushing first;
 * - the pending `awaitingInput.question` is appended as a trailing `{kind:'note'}`, unless
 *   it's already shown (as the last turn's reasoning or note) or the human just answered it.
 */
export function operatorActivityToChatItems(
  activity: ChatSource | null,
  sentReplies: readonly SentReply[],
): ChatItem[] {
  const out: ChatItem[] = [];
  const entries = activity?.messages ?? [];
  const screenshots = activity?.screenshots ?? [];

  let open: OpenTurn | null = null;
  const flushTurn = () => {
    if (!open) return;
    out.push({
      kind: 'turn',
      id: open.id,
      timestamp: open.timestamp,
      reasoning: open.reasoning,
      actions: open.actions,
      screenshotDataUrl: open.screenshotDataUrl,
      section: open.section,
    });
    open = null;
  };

  const repliesByAnchor = new Map<number, SentReply[]>();
  for (const reply of sentReplies) {
    const list = repliesByAnchor.get(reply.afterCount);
    if (list) list.push(reply);
    else repliesByAnchor.set(reply.afterCount, [reply]);
  }
  const flushReplies = (anchor: number) => {
    for (const reply of repliesByAnchor.get(anchor) ?? []) {
      flushTurn();
      out.push({
        id: reply.id,
        kind: 'human',
        content: reply.content,
        timestamp: new Date(0),
      });
    }
  };

  flushReplies(0);
  entries.forEach((entry, i) => {
    const metaKind = entry.metadata?.kind;
    if (metaKind === SOP_STEP_KIND) {
      flushTurn();
      out.push({
        id: entry.id,
        kind: 'marker',
        content: recordedStepLine(entry),
        timestamp: new Date(entry.timestamp),
      });
    } else if (
      metaKind === PRE_AUTH_SKIPPED_KIND ||
      metaKind === PRE_AUTH_SUCCEEDED_KIND
    ) {
      flushTurn();
      out.push({
        id: entry.id,
        kind: 'system',
        content: entry.message,
        timestamp: new Date(entry.timestamp),
        tone: preAuthTone(entry),
      });
    } else if (isSetupProcedureEntry(entry)) {
      if (!entry.action && entry.message.trim()) {
        flushTurn();
        out.push({
          id: entry.id,
          kind: 'note',
          content: entry.message,
          timestamp: new Date(entry.timestamp),
          badge: SETUP_PROCEDURE_BADGE,
        });
      }
      // else: a setup-procedure action entry — dropped, as today (a different
      // procedure's telemetry, not part of this interview's turns).
    } else if (entry.action === SEED_ACTION) {
      // Environment setup — show its message as a readable line under the "Setup" section
      // (tagged section:"setup" by the backend). A standalone turn so nothing folds into it.
      if (entry.message.trim()) {
        flushTurn();
        open = {
          id: entry.id,
          timestamp: new Date(entry.timestamp),
          reasoning: entry.message,
          actions: [],
          screenshotDataUrl: null,
          section: { phase: 'setup' },
        };
        flushTurn();
      }
    } else if (!entry.action && entry.message.trim()) {
      const shot = screenshotFor(entry.messageId, screenshots);
      if (open && open.reasoning === null) {
        open.reasoning = entry.message;
        if (shot) open.screenshotDataUrl = shot;
      } else {
        flushTurn();
        open = {
          id: entry.id,
          timestamp: new Date(entry.timestamp),
          reasoning: entry.message,
          actions: [],
          screenshotDataUrl: shot,
          section: null,
        };
      }
    } else if (entry.action === NEXT_SCREEN) {
      // A control action, not a build beat — grouping is driven by the `section` metadata on the
      // edits/renders, so just close the current turn (its "on to the next screen" reasoning stays
      // with the screen it finished) and drop the row.
      flushTurn();
    } else if (entry.action) {
      const entrySection = sectionOf(entry);
      // A section change mid-stream (e.g. the last screen's edit → authoring eval-paths.json)
      // starts a fresh turn, so a screen's actions never fold into the finalize section.
      if (
        open &&
        entrySection &&
        open.section &&
        sectionKey(entrySection) !== sectionKey(open.section)
      ) {
        flushTurn();
      }
      if (!open) {
        open = {
          id: entry.id,
          timestamp: new Date(entry.timestamp),
          reasoning: null,
          actions: [],
          screenshotDataUrl: null,
          section: null,
        };
      }
      open.actions.push({
        id: entry.id,
        action: entry.action,
        detail: actionDetail(entry.action, entry.metadata),
      });
      const shot = screenshotFor(entry.messageId, screenshots);
      if (shot) open.screenshotDataUrl = shot;
      open.section = entrySection ?? open.section;
    }
    // else: an empty entry (no action, no text) — skip, as today.
    flushReplies(i + 1);
  });
  flushTurn();

  const question = activity?.awaitingInput?.question?.trim();
  if (question) {
    const last = out.at(-1);
    const alreadyShown =
      last?.kind === 'turn'
        ? last.reasoning?.trim() === question
        : last?.kind === 'note'
          ? last.content.trim() === question
          : false;
    const justAnswered = last?.kind === 'human';
    if (!alreadyShown && !justAnswered) {
      out.push({
        id: `awaiting-${entries.length}`,
        kind: 'note',
        content: question,
        timestamp: new Date(0),
      });
    }
  }

  return withSections(out);
}

/** Insert `{kind:'section'}` dividers so the panel can group the thread into Setup / Screen N /
 * Finalize. A divider opens whenever a turn's section changes (screens differ by index). Only
 * kicks in when the build actually has screens; a plain modify build (no storyboard) stays a flat
 * thread, as before. Reasoning-only turns and non-turn items inherit the current section. */
function withSections(items: ChatItem[]): ChatItem[] {
  const hasScreens = items.some(
    (it) =>
      it.kind === 'turn' &&
      (it.section?.phase === 'screen' || it.section?.phase === 'finalize'),
  );
  if (!hasScreens) return items;
  const out: ChatItem[] = [];
  let currentKey: string | null = null;
  for (const item of items) {
    if (item.kind === 'turn' && item.section) {
      const key = sectionKey(item.section);
      if (key !== currentKey) {
        currentKey = key;
        out.push({
          kind: 'section',
          id: `section-${item.id}`,
          section: item.section,
          timestamp: item.timestamp,
        });
      }
    }
    out.push(item);
  }
  return out;
}
